import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';

/**
 * Runtime machine translation for stored free text — notes, reasons, addresses — which cannot live
 * in the static dictionary because it is typed by users.
 *
 * Design points that matter:
 *
 * - The provider key is NOT here. Requests go to our own /translate endpoint, which holds the key
 *   server-side. A key in the bundle is readable by anyone and billable by anyone.
 * - Calls are batched. Every string that renders in one tick is collected and sent as one request
 *   rather than one request per table cell, which is the difference between a handful of calls and
 *   several hundred per page.
 * - Results are cached in memory and in localStorage, so a given string is paid for once per
 *   browser rather than once per render.
 * - Failure is invisible: the original text renders. A translation outage must never blank a
 *   financial record.
 *
 * What deliberately does NOT go through here: names, account codes, CNIC numbers, amounts and
 * dates. Translating a name corrupts it — "Rana Asif Ali" is not a phrase to be rendered into
 * Urdu — and figures must stay byte-identical to the receipts and the database.
 */

const STORAGE_KEY = 'ims.translationCache';
const MAX_BATCH = 200;

type Cache = Record<string, string>;

const memory: Cache = loadCache();

function loadCache(): Cache {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}') as Cache;
  } catch {
    return {};
  }
}

let persistTimer: number | undefined;
function persist() {
  window.clearTimeout(persistTimer);
  persistTimer = window.setTimeout(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(memory));
    } catch {
      // Quota exceeded — the in-memory cache still works for this session.
    }
  }, 500);
}

const key = (lang: string, text: string) => `${lang}|${text}`;

// ---------- batching ----------

let queue = new Map<string, ((value: string) => void)[]>();
let flushTimer: number | undefined;

/** Collects everything requested in this tick, then sends it as one request. */
function enqueue(lang: string, text: string): Promise<string> {
  return new Promise((resolve) => {
    const waiting = queue.get(text) ?? [];
    waiting.push(resolve);
    queue.set(text, waiting);

    window.clearTimeout(flushTimer);
    flushTimer = window.setTimeout(() => void flush(lang), 50);
  });
}

async function flush(lang: string) {
  const batch = queue;
  queue = new Map();
  if (batch.size === 0) return;

  const texts = [...batch.keys()].slice(0, MAX_BATCH);

  try {
    const { data } = await api.post<{ translations: string[]; enabled: boolean }>('/translate', {
      texts,
      target: lang,
    });

    texts.forEach((text, i) => {
      const value = data.translations?.[i] ?? text;
      memory[key(lang, text)] = value;
      batch.get(text)?.forEach((resolve) => resolve(value));
    });
    persist();
  } catch {
    // Endpoint unreachable or translation disabled — render the source text unchanged.
    texts.forEach((text) => batch.get(text)?.forEach((resolve) => resolve(text)));
  }
}

/**
 * Translates many strings at once and returns a source→translation map. Used by the page-level
 * translator, which already has its whole set in hand and does not need per-string batching.
 * Anything cached is served locally; only the remainder is requested.
 */
export async function translateBatch(texts: string[], lang: string): Promise<Record<string, string>> {
  const out: Record<string, string> = {};
  const missing: string[] = [];

  for (const text of texts) {
    const hit = memory[key(lang, text)];
    if (hit) out[text] = hit;
    else missing.push(text);
  }

  if (missing.length === 0 || lang === 'en') return out;

  // The endpoint caps a request at 200 strings.
  for (let i = 0; i < missing.length; i += MAX_BATCH) {
    const slice = missing.slice(i, i + MAX_BATCH);
    try {
      const { data } = await api.post<{ translations: string[] }>('/translate', {
        texts: slice,
        target: lang,
      });
      slice.forEach((text, j) => {
        const value = data.translations?.[j] ?? text;
        memory[key(lang, text)] = value;
        out[text] = value;
      });
    } catch {
      // Unreachable or disabled — leave these untranslated rather than blanking the page.
      slice.forEach((text) => (out[text] = text));
    }
  }

  persist();
  return out;
}

// ---------- hook ----------

/**
 * Returns `text` translated into the active language, or `text` itself while the request is in
 * flight, when the language is English, or when translation is unavailable.
 */
export function useAutoTranslate(text: string | null | undefined): string {
  const { i18n } = useTranslation();
  const lang = i18n.language.startsWith('ur') ? 'ur' : 'en';

  const cached = text ? memory[key(lang, text)] : undefined;
  const [value, setValue] = useState(cached ?? text ?? '');

  useEffect(() => {
    if (!text) {
      setValue('');
      return;
    }

    // English is the source language — nothing to do, and no reason to spend a call on it.
    if (lang === 'en') {
      setValue(text);
      return;
    }

    const hit = memory[key(lang, text)];
    if (hit) {
      setValue(hit);
      return;
    }

    let live = true;
    setValue(text);
    void enqueue(lang, text).then((translated) => {
      if (live) setValue(translated);
    });

    return () => {
      live = false;
    };
  }, [text, lang]);

  return value;
}
